import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RollbackOutlined } from '@ant-design/icons';
import { canUseInstitutionSwitcher, useAuth } from '../../auth/AuthContext';
import {
  cancelWorkspaceQueries,
  clearWorkspaceCaches,
} from '../../auth/workspaceCaches';
import './InstitutionContextBanner.css';

const formatAssumedRoleLabel = (role: string): string =>
  role
    .split('-')
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const formatInstitutionLabel = (
  institutionName: string | null,
  institutionId: string | null,
  fallbackLabel: string,
): string => institutionName?.trim() || institutionId || fallbackLabel;

export const InstitutionContextBanner: React.FC = () => {
  const { t } = useTranslation('common');
  const {
    originalUser,
    user,
    assumedRole,
    assumedInstitutionId,
    assumedInstitutionName,
    resetAssumedRole,
    institutionSwitcherEnabled,
  } = useAuth();
  const navigate = useNavigate();

  if (
    !canUseInstitutionSwitcher(originalUser ?? user, institutionSwitcherEnabled) ||
    !assumedRole ||
    !assumedInstitutionId
  ) {
    return null;
  }

  const roleLabel = formatAssumedRoleLabel(assumedRole);
  const institutionLabel = formatInstitutionLabel(
    assumedInstitutionName,
    assumedInstitutionId,
    t('institutionBanner.selectedInstitution'),
  );

  return (
    <section className="institution-context-banner" role="status" aria-live="polite">
      <div className="institution-context-banner__content">
        <span className="institution-context-banner__eyebrow">
          {t('institutionBanner.eyebrow')}
        </span>
        <h2 className="institution-context-banner__title">
          {t('institutionBanner.title', { role: roleLabel, institution: institutionLabel })}
        </h2>
        <p className="institution-context-banner__description">
          {t('institutionBanner.description')}
        </p>
      </div>

      <div className="institution-context-banner__actions">
        <button
          type="button"
          className="institution-context-banner__button institution-context-banner__button--primary"
          onClick={async () => {
            await cancelWorkspaceQueries();
            resetAssumedRole();
            clearWorkspaceCaches();
            navigate('/', { replace: true });
          }}
        >
          <RollbackOutlined />
          <span>{t('institutionBanner.returnButton')}</span>
        </button>
      </div>
    </section>
  );
};

export default InstitutionContextBanner;
